import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';

interface FloatingAvatarProps {
  mousePosition: { x: number; y: number };
  onClick?: () => void;
}

const FloatingAvatar = ({ mousePosition, onClick }: FloatingAvatarProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 400;
    canvas.height = 400;

    // Particle system for orbiting particles
    interface Particle {
      angle: number;
      radius: number;
      speed: number;
      size: number;
    }

    const particles: Particle[] = Array.from({ length: 30 }, () => ({
      angle: Math.random() * Math.PI * 2,
      radius: 80 + Math.random() * 60,
      speed: 0.005 + Math.random() * 0.01,
      size: 2 + Math.random() * 3,
    }));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Draw orbiting particles
      particles.forEach((particle) => {
        particle.angle += particle.speed;

        const x = centerX + Math.cos(particle.angle) * particle.radius;
        const y = centerY + Math.sin(particle.angle) * particle.radius;

        // Draw particle with glow
        ctx.beginPath();
        ctx.arc(x, y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(59, 130, 246, ${0.6 + Math.sin(particle.angle) * 0.4})`;
        ctx.shadowBlur = 10;
        ctx.shadowColor = 'rgba(59, 130, 246, 0.8)';
        ctx.fill();
        ctx.shadowBlur = 0;

        // Draw connection lines to center
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.strokeStyle = `rgba(59, 130, 246, 0.1)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      requestAnimationFrame(animate);
    };

    animate();
  }, []);

  return (
    <motion.div
      className="relative mb-12"
      animate={{
        x: mousePosition.x,
        y: mousePosition.y,
      }}
      transition={{ type: 'spring', stiffness: 50, damping: 15 }}
    >
      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ width: '400px', height: '400px' }}
      />

      {/* Outer glow rings */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <motion.div
          className="w-64 h-64 rounded-full border-2 border-blue-500/20"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.2, 0.5],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-0 left-0 w-64 h-64 rounded-full border-2 border-cyan-400/20"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.1, 0.3],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        />
      </div>

      {/* MacMind Logo/Avatar */}
      <motion.div
        className="relative w-48 h-48 md:w-64 md:h-64 flex items-center justify-center cursor-pointer"
        animate={{
          y: [0, -10, 0],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        onClick={onClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Brain Icon with Glow */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full blur-3xl opacity-40 animate-pulse-slow" />
          <svg
            className="w-32 h-32 md:w-40 md:h-40 relative z-10 text-blue-400 drop-shadow-2xl"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M21.33 8.6c-1.67-1.67-3.77-2.6-5.94-2.6-1.48 0-2.9.38-4.11 1.09C10.07 6.38 8.65 6 7.17 6c-2.17 0-4.27.93-5.94 2.6-1.67 1.67-2.6 3.77-2.6 5.94 0 1.48.38 2.9 1.09 4.11C.38 19.86 0 21.28 0 22.76v.01c0 .41.34.75.75.75h22.5c.41 0 .75-.34.75-.75v-.01c0-1.48-.38-2.9-1.09-4.11.71-1.21 1.09-2.63 1.09-4.11 0-2.17-.93-4.27-2.6-5.94zm-14.16 0c1.11-1.11 2.51-1.67 3.94-1.67.98 0 1.92.24 2.76.7-.84.45-1.63 1.02-2.33 1.72-1.67 1.67-2.6 3.77-2.6 5.94 0 .99.17 1.95.48 2.85-.84-.45-1.63-1.02-2.33-1.72-1.11-1.11-1.67-2.51-1.67-3.94s.56-2.83 1.67-3.94zm7.94 7.94c-1.11 1.11-2.51 1.67-3.94 1.67-.98 0-1.92-.24-2.76-.7.84-.45 1.63-1.02 2.33-1.72 1.67-1.67 2.6-3.77 2.6-5.94 0-.99-.17-1.95-.48-2.85.84.45 1.63 1.02 2.33 1.72 1.11 1.11 1.67 2.51 1.67 3.94s-.56 2.83-1.67 3.94z" />
          </svg>
        </div>

        {/* Rotating ring */}
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        >
          <div className="w-full h-full border-2 border-dashed border-blue-400/30 rounded-full" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default FloatingAvatar;
